<template>
  <client-only>
    <TransitionRoot :show="isOpen" as="template">
      <Dialog
        component-name="modal-bath-concept-zoom"
        data-skin="black"
        class="relative z-50 !bg-transparent"
        data-lenis-prevent
      >
        <!-- Full-screen scrollable container -->
        <div class="pointer-events-none overflow-clip fixed inset-0">
          <!-- Container to center the panel -->
          <div class="flex flex-col w-full h-full">
            <!-- The actual dialog panel -->
            <component
              :is="$device.isMobile ? 'div' : DialogPanel"
              class="relative top-0 left-0 select-none pointer-events-auto flex flex-col h-screen max-h-screen pb-[calc(100lvh-100svh)] !m-0 !p-0"
            >
              <btn-icon
                ref="tpl_btnClose"
                :title="$t.labels.close"
                class="absolute top-edge right-edge z-3"
                variant="rounded-filled"
                icon="close"
                @click="zoom(false)"
              />

              <!-- GALLERY -->
              <div
                ref="tpl_swiperWrapper"
                class="w-full h-full"
                :class="{ invisible: !entryDone }"
              >
                <carousel-bath-concept-zoom
                  ref="tpl_swiper"
                  :items="transformedItemsBig"
                  :itemIndex="itemIndex"
                  class="w-full h-full cursor-zoom-out bg-skin-base"
                  @click="zoom(false)"
                />
              </div>
              <!-- end GALLERY -->

              <!-- ZOOM FX WRAPPER -->
              <div
                ref="tpl_zoomTarget"
                inert
                class="absolute z-2 top-0 left-0 w-full h-auto"
                :class="{ invisible: entryDone }"
              >
                <template v-for="(item, i) in transformedItemsBig">
                  <img
                    v-if="i === itemIndex"
                    :alt="item.alt"
                    :src="fromCloudinary(item.src, { width: 2560 })"
                    :width="item.width"
                    :height="item.height"
                    loading="eager"
                    class="block max-w-full w-full h-auto object-contain object-bottom"
                    :class="{
                      'aspect-1/1': item.aspectRatio === '1 / 1',
                      'aspect-4/5': item.aspectRatio === '4 / 5',
                    }"
                  />
                </template>
              </div>
              <!-- end ZOOM FX WRAPPER -->

              <!-- MENU -->
              <footer
                ref="tpl_footer"
                class="origin-top-left absolute z-4 bottom-edge left-0 flex gap-lg justify-center w-full px-edge"
              >
                <div
                  class="overflow-x-scroll flex gap-lg justify-center min-w-min rounded-xl p-lg bg-black/30 hover:bg-black/80 hover:duration-400 transition-colors duration-700 ease-out"
                >
                  <btn
                    v-for="(item, i) in transformedItemsThumbs"
                    :title="item.alt"
                    :disabled="itemIndex === i"
                    class="after:block after:w-3/5 after:h-0.5 after:bg-white after:mx-auto after:relative after:transition-opacity after:ease-in-out cursor-pointer"
                    :class="{
                      'after:-bottom-2.5 after:opacity-0 after:duration-400':
                        itemIndex !== i,
                      'after:-bottom-2 after:opacity-100 after:duration-700':
                        itemIndex === i,
                    }"
                    @click="$emit('update', i)"
                  >
                    <div class="overflow-hidden">
                      <img
                        :alt="item.alt"
                        :src="item.src"
                        :width="item.width"
                        :height="item.height"
                        loading="eager"
                        class="block h-24 w-auto group-hover/btn:scale-110 transition-transform duration-500"
                        :class="{
                          'aspect-1/1': item.aspectRatio === '1 / 1',
                          'aspect-4/5': item.aspectRatio === '4 / 5',
                        }"
                      />
                    </div>
                  </btn>
                </div>
              </footer>
              <!-- end MENU -->
            </component>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </client-only>
</template>

<script setup>
const { worker, result, sendMessage } = useWebWorker(
  "/workers/image-loader.js"
);

import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/vue";

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: false,
    default: false,
  },
  items: {
    type: Array,
    required: true,
  },
  itemIndex: {
    type: Number,
    required: false,
    default: 0,
  },
  fitElement: {
    type: Object,
    validator: function (value) {
      // Expected to be an HTML element
      return value instanceof HTMLElement;
    },
    required: false,
  },
});

const { fromCloudinary } = useCdn();

function transformItems(size = 2560) {
  return props.items.map((item) => {
    return {
      ...item,
      src: fromCloudinary(item.src, { width: size }),
    };
  });
}
const transformedItemsBig = ref();
transformedItemsBig.value = transformItems(2560);

const transformedItemsThumbs = ref();
transformedItemsThumbs.value = transformItems(100);

// create an array of images to preload, add the item[itemIndex] at the beginning of the array
const preloadImages = computed(() => {
  const itemsBig = transformedItemsBig.value.map((image) => image.src);
  const itemsThumbs = transformedItemsThumbs.value.map((image) => image.src);
  return [
    itemsBig[props.itemIndex],
    ...itemsThumbs,
    ...itemsBig.filter((item, i) => i !== props.itemIndex),
  ];
});

const route = useRoute();
const entryDone = ref(false);
const tpl_btnClose = templateRef("tpl_btnClose");
const tpl_swiperWrapper = templateRef("tpl_swiperWrapper");
const tpl_swiper = templateRef("tpl_swiper");
const tpl_zoomTarget = templateRef("tpl_zoomTarget");
const tpl_footer = templateRef("tpl_footer");
const emit = defineEmits(["close", "update"]);
let tl;
let swiperEl;

function zoom(zoomIn = true) {
  if (tl) {
    tl.kill();
    tl = null;
  }

  if (zoomIn) {
    entryDone.value = false;
  }

  tl = gsap.timeline({
    onComplete: () => {
      if (zoomIn) {
        entryDone.value = true;
      } else {
        emitClose();
      }
    },
  });

  if (zoomIn) {
    tl.add(Flip.fit(tpl_zoomTarget.value, props.fitElement));

    tl.add(
      Flip.fit(tpl_zoomTarget.value, tpl_swiperWrapper.value, {
        duration: 0.75,
        ease: "expo.inOut",
      })
    );

    tl.fromTo(
      tpl_footer.value,
      { y: 200, rotate: 3 },
      { duration: 0.7, y: 0, rotate: 0, ease: "power3.inOut" },
      "<0.2"
    );

    tl.fromTo(
      tpl_btnClose.value.$el,
      { x: 300, rotate: 180 },
      { duration: 1, x: 0, rotate: 0, ease: "expo.inOut" },
      "<0.1"
    );
  } else {
    if (tpl_swiper.value.getActiveSlideEl().scrollTop > 0) {
      tl.to(tpl_swiper.value.getActiveSlideEl(), {
        duration: 0.25,
        scrollTo: 0,
        ease: "sine.inOut",
      });
    }

    tl.to(tpl_footer.value, {
      onStart: () => {
        entryDone.value = false;
      },
      duration: 0.7,
      y: 200,
      rotate: 3,
      ease: "power3.inOut",
    });

    tl.to(
      tpl_btnClose.value.$el,
      { duration: 0.6, x: 200, rotate: 180, ease: "expo.inOut" },
      "<"
    );

    tl.add(
      Flip.fit(tpl_zoomTarget.value, props.fitElement, {
        scale: false,
        duration: 0.75,
        ease: "expo.inOut",
      }),
      "<0.1"
    );
  }
}

function emitClose() {
  emit("close");
}

watch(
  () => route.fullPath,
  (newVal, oldVal) => {
    emitClose();
  }
);

onMounted(() => {
  window.addEventListener("reload-same-page", emitClose);

  sendMessage(preloadImages.value);

  watch(
    () => props.items,
    (newVal, oldVal) => {
      transformedItemsBig.value = transformItems(2560);
      transformedItemsThumbs.value = transformItems(100);
      nextTick(() => {
        sendMessage(preloadImages.value);
      });
    }
  );
});

onBeforeUnmount(() => {
  window.removeEventListener("reload-same-page", emitClose);
});

defineExpose({ zoom });
</script>
